/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IGetActiveMenusResponse, IMenuPriceSearchArgs } from '../custom-actions/DataServiceEntities.g';
import { getActiveMenusForChannelAsync, lAC_GetActiveMenusAsync } from '../custom-actions/DataActionExtension.g';
import { getAuthToken } from '../utils/cookieutils';
import { withoutExcludedFromEcom } from '../utils/ecommenu';

export class GetCategoryMenuInput implements Msdyn365.IActionInput {
    public eventId: string;
    public menuPriceSearchArgs: IMenuPriceSearchArgs;
    public channelId: number;
    public lookAhead: boolean;
    public includeDescription: boolean;
    public languageId: string;
    public forceIncludeProductImages: boolean;
    constructor(
        eventId: string,
        menuPriceSearchArgs: IMenuPriceSearchArgs,
        channelId: number,
        lookAhead: boolean,
        includeDescription: boolean,
        languageId: string,
        forceIncludeProductImages: boolean
    ) {
        this.eventId = eventId;
        this.menuPriceSearchArgs = menuPriceSearchArgs;
        this.channelId = channelId;
        this.lookAhead = lookAhead;
        this.includeDescription = includeDescription;
        this.languageId = languageId;
        this.forceIncludeProductImages = forceIncludeProductImages;
    }
    // Every field the response varies by has to be part of the key. With a constant key the first caller to
    // run wins for everyone: hs-search fires on mount with channelId 0, before the header has written the
    // channelid cookie, so later callers passing a correct channelId were served that empty response.
    public getCacheKey = () => {
        const { CustomerId = '', IncludeMenuItemPrices = false } = this.menuPriceSearchArgs || {};
        return [
            'GETMENUITEMS',
            this.channelId,
            this.eventId,
            CustomerId,
            IncludeMenuItemPrices,
            this.lookAhead,
            this.includeDescription,
            this.languageId,
            this.forceIncludeProductImages
        ].join('_');
    };
    public getCacheObjectType = () => 'GETMENUITEMS';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}
// A failure has to reject. The observable action caches whatever this resolves, so an empty menu handed
// back in place of an error is kept under the correct key and served to every later caller for the life of
// the page, with nothing left to say the call never landed.
async function getCategoryMenuAsync(input: GetCategoryMenuInput, ctx: Msdyn365.IActionContext): Promise<IGetActiveMenusResponse> {
    const authToken = getAuthToken();
    if (authToken.Token && authToken.IntuitDomeAccountId) {
        try {
            const menus = await lAC_GetActiveMenusAsync(
                { callerContext: ctx },
                authToken,
                input.eventId,
                input.channelId,
                input.lookAhead,
                input.includeDescription,
                input.languageId,
                input.forceIncludeProductImages
            );

            return withoutExcludedFromEcom(menus ?? {});
        } catch (error) {
            console.log('LTS Info', 'get-category-menu: the signed-in menu call failed, asking without the token', error);
        }
    }

    const result = await getActiveMenusForChannelAsync(
        { callerContext: ctx },
        input.eventId,
        input.menuPriceSearchArgs,
        input.channelId,
        input.lookAhead,
        input.includeDescription,
        input.languageId,
        input.forceIncludeProductImages
    );

    return withoutExcludedFromEcom(result ?? {});
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetActiveMenusResponse>>getCategoryMenuAsync,
    id: 'GetCategoryMenu'
});
